import { useMemo } from 'react'
import {
  type DrawingDataStructure,
  type Snapshot,
  calculateCanvasBounds,
  extractSnapshots,
  generateSnapshotSVG,
} from '../utils/drawingUtils'

interface SnapshotGalleryProps {
  drawingData: DrawingDataStructure
}

export function SnapshotGallery({ drawingData }: SnapshotGalleryProps) {
  const snapshots = useMemo(() => {
    if (!Array.isArray(drawingData)) return []
    return extractSnapshots(drawingData, 1000) // 1000ms 이상 간격
  }, [drawingData])

  const bounds = useMemo(() => {
    if (!Array.isArray(drawingData)) return null
    return calculateCanvasBounds(drawingData)
  }, [drawingData])

  if (!bounds || snapshots.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        스냅샷이 없습니다 (1초 이상 간격의 구간 없음)
      </div>
    )
  }

  return (
    <div>
      <div className="mb-3 text-sm text-gray-600">
        {snapshots.length}개의 스냅샷 (1초 이상 간격 구간)
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {snapshots.map((snapshot: Snapshot, index: number) => {
          const svgContent = generateSnapshotSVG(snapshot.strokes, bounds, snapshot.timeMs, snapshot.startTimeMs)
          const dataUrl = `data:image/svg+xml;base64,${btoa(svgContent)}`
          const absoluteTimeSec = snapshot.timeMs / 1000 // 절대 시간 (초)

          return (
            <div key={index} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <div className="aspect-square bg-gray-50 flex items-center justify-center p-4">
                <img
                  src={dataUrl}
                  alt={`Snapshot ${index + 1}`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="px-3 py-2 border-t border-gray-200">
                <div className="text-xs text-gray-600">
                  스냅샷 #{index + 1} · {absoluteTimeSec.toFixed(4)}s
                </div>
                <div className="text-xs text-gray-500">
                  {snapshot.strokes.length} strokes
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
